import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaBook, FaSearch } from 'react-icons/fa';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import colors from '../constants/colors';

export default function ManageSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [subjectName, setSubjectName] = useState('');
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSubjects();
  }, []);

  const getAuthToken = () => localStorage.getItem('authToken'); // Retrieve token from localStorage

  const fetchSubjects = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getAuthToken();
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get(
        `${appConstants.API_ROOT_URL}/get-subjects`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSubjects(response.data.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setError('Failed to load subjects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subjectName.trim()) {
      alert('Subject name is required.');
      return;
    }

    setSaving(true);
    try {
      const token = getAuthToken();
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const url = editId
        ? `${appConstants.API_ROOT_URL}/update-subject`
        : `${appConstants.API_ROOT_URL}/create-subject`;
      await axios.post(
        url,
        { id: editId, name: subjectName },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      handleCancel();
      setShowModal(false);
      fetchSubjects();
    } catch (error) {
      console.error('Error saving subject:', error);
      alert('Failed to save subject. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (subject) => {
    setSubjectName(subject.name);
    setEditId(subject.id);
    setShowModal(true);
  };

  const handleShowModal = () => {
    setSubjectName('');
    setEditId(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        const token = getAuthToken();
        if (!token) {
          console.error('No authentication token found');
          return;
        }

        await axios.post(
          `${appConstants.API_ROOT_URL}/delete-subject`,
          { id },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        fetchSubjects();
      } catch (error) {
        console.error('Error deleting subject:', error);
      }
    }
  };

  const handleCancel = () => {
    setSubjectName('');
    setEditId(null);
  };

  // Filter subjects based on search term
  const filteredSubjects = subjects.filter((subject) =>
    subject.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Layout>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Compact Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '12px',
            backgroundColor: colors.sidebar.background,
            padding: '6px',
            borderRadius: '6px',
            color: colors.sidebar.text,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: '1rem',
                color: 'white',
                marginRight: 16,
              }}
            >
              <FaBook />
              Subjects
            </span>

            {/* Search Input */}
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.sidebar.text,
                  fontSize: '0.9rem',
                }}
              />
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  paddingLeft: '35px',
                  height: '30px',
                  backgroundColor: colors.card.background,
                  borderColor: colors.card.border,
                  color: colors.text.primary,
                }}
              />
            </div>
          </div>

          {/* Add Subject Button */}
          <Button
            onClick={handleShowModal}
            variant="light"
            size="sm"
            disabled={saving}
            style={{
              width: '32px',
              height: '28px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
              fontSize: '0.85rem',
              fontWeight: '600',
            }}
            title="Add Subject"
          >
            <FaPlus />
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            onClose={() => setError('')}
            dismissible
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Content Section */}
        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Spinner
              animation="border"
              style={{ color: '#667eea', width: '50px', height: '50px' }}
            />
            <p style={{ marginTop: '20px', color: '#6c757d' }}>
              Loading subjects...
            </p>
          </div>
        ) : filteredSubjects.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <FaBook
              style={{
                fontSize: '4rem',
                color: '#dee2e6',
                marginBottom: '20px',
              }}
            />
            <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>
              No Subjects Found
            </h4>
            <p style={{ color: '#adb5bd' }}>
              {searchTerm
                ? 'No subjects match your search criteria.'
                : 'Get started by creating your first subject.'}
            </p>
            {!searchTerm && (
              <Button
                onClick={handleShowModal}
                style={{
                  marginTop: '20px',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                <FaPlus className="me-2" /> Create Subject
              </Button>
            )}
          </div>
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <Table striped hover style={{ marginBottom: 0 }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: 'white',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '80px',
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                      }}
                    >
                      Subject Name
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                        width: '120px',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubjects.map((subject, index) => (
                    <tr
                      key={subject.id}
                      style={{
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#6c757d',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#2c3e50',
                          fontWeight: '500',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {subject.name}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(subject)}
                          disabled={saving}
                          style={{
                            borderRadius: '6px',
                            padding: '6px 10px',
                            fontSize: '0.85rem',
                          }}
                          title="Edit Subject"
                        >
                          <FaEdit />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Summary Footer */}
            <div
              style={{
                padding: '16px 20px',
                background: '#f8f9fa',
                borderTop: '2px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
              }}
            >
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                Showing {filteredSubjects.length} subjects
                {searchTerm && ' (filtered)'}
              </span>
            </div>
          </div>
        )}

        {/* Add/Edit Subject Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderBottom: 'none',
            }}
          >
            <Modal.Title style={{ fontWeight: '700' }}>
              {editId ? 'Edit Subject' : 'Add New Subject'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '30px' }}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Subject Name <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={subjectName}
                  onChange={(e) => setSubjectName(e.target.value)}
                  placeholder="Enter subject name"
                  required
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    padding: '10px 15px',
                    transition: 'all 0.3s ease',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.boxShadow =
                      '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer
            style={{ borderTop: '1px solid #dee2e6', padding: '20px 30px' }}
          >
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              disabled={saving}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !subjectName.trim()}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
              }}
            >
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                <>{editId ? 'Update Subject' : 'Create Subject'}</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Layout>
  );
}
